"use client";

import React, { Context, createContext, useContext, useEffect, useState } from "react";
import { find } from "lodash";

import { QREditorContext } from "../QREditorContext";

import { FeedbackReview, QRFeedbackForm, QRFeedbackReviewCategory } from "@/types/qr";
import { QREditorContextProps } from "../QREditorContext/types";
import { QRFeedbackContextProps } from "./types";

let QRFeedbackContext: Context<QRFeedbackContextProps>;
const { Provider } = (QRFeedbackContext = createContext<QRFeedbackContextProps>(undefined));

const QRFeedbackProvider = ({ children }: any) => {
  const [currentView, setCurrentView] = useState(1);

  const qrEditorContext = useContext<QREditorContextProps<"feedback">>(QREditorContext);

  const [feedbackReviewData, setFeedbackReviewData] = useState<FeedbackReview>({
    categories: qrEditorContext?.qrData?.data?.categories as QRFeedbackReviewCategory[],
    contactData: {
      name: "",
      email: "",
      phone: "",
    },
    createdAt: "",
    id: "",
    rateAverage: 0,
    ratingBase: 0,
    updatedAt: "",
  });

  const [selectedReviewCategory, setSelectedReviewCategory] = useState<
    QRFeedbackReviewCategory | undefined
  >(feedbackReviewData.categories?.[0]);

  const [reviewContactData, setReviewContactData] = useState(undefined);

  const [ratingSelected, setRatingSelected] = useState(undefined);

  const setSelectedReviewCategoryIndex = (index: number) => {
    const category = feedbackReviewData.categories?.[index];
    setSelectedReviewCategory(category);
  };

  useEffect(() => {
    if (!qrEditorContext.qrData.data) return;

    setFeedbackReviewData((prev) => {
      const editorData = qrEditorContext.qrData.data;
      const updatedCategoryNames = editorData?.categories?.map((category) => ({
        ...category,
        rating: find(prev.categories, { id: category.id })?.rating ?? null,
        comment: find(prev.categories, { id: category.id })?.comment ?? "",
      }));

      const form = editorData?.form as QRFeedbackForm | undefined;
      const updatedData = { ...prev.contactData };
      if (form) {
        (["name", "phone", "email"] as const).forEach((key) => {
          if (form[key] === true) {
            updatedData[key] = prev.contactData[key] || "";
          }
        });
      }

      return {
        ...prev,
        categories: updatedCategoryNames,
        contactData: updatedData,
      };
    });
  }, [qrEditorContext.qrData.data]);

  useEffect(() => {
    const cats = feedbackReviewData.categories;
    if (!cats?.length) {
      setSelectedReviewCategory(undefined);
      return;
    }
    setSelectedReviewCategory((prev) => {
      if (!prev) {
        return cats[0];
      }
      const match = cats.find((c) => c.id === prev.id);
      return match ?? cats[0];
    });
  }, [feedbackReviewData.categories]);

  return (
    <Provider
      value={{
        feedbackReviewData,
        setFeedbackReviewData,
        selectedReviewCategory,
        setSelectedReviewCategory,
        setSelectedReviewCategoryIndex,
        reviewContactData,
        setReviewContactData,
        ratingSelected,
        setRatingSelected,
        currentView,
        setCurrentView,
      }}
    >
      {children}
    </Provider>
  );
};

export { QRFeedbackContext, QRFeedbackProvider };
